import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Package, Hash, QrCode, Cpu, Command } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCommandPaletteStore } from '@/hooks/useCommandPalette';
import { listWorkOrders } from '@/data/mockWorkOrders';

interface SearchResult {
  id: string;
  label: string;
  sub?: string;
  path: string;
}

interface ResultGroup {
  heading: string;
  Icon: React.ComponentType<{ className?: string }>;
  items: SearchResult[];
}

// Mock SN, SSCC, machines (WO comes from real mock dataset)
const MOCK_SN: SearchResult[] = [
  { id: 'SN-78234', label: 'SN-78234', sub: 'Panel PV400 · WO-2026-0042', path: '/traceability-search?q=SN-78234' },
  { id: 'SN-78235', label: 'SN-78235', sub: 'Panel PV400 · WO-2026-0042', path: '/traceability-search?q=SN-78235' },
  { id: 'SN-91022', label: 'SN-91022', sub: 'Módulo Elevación · WO-2026-0046', path: '/traceability-search?q=SN-91022' },
];

const MOCK_SSCC: SearchResult[] = [
  { id: 'SSCC-00370123450000000017', label: '00370123450000000017', sub: 'Palet · LINE-A', path: '/traceability-search?q=00370123450000000017' },
  { id: 'SSCC-00370123450000000024', label: '00370123450000000024', sub: 'Palet · LINE-B', path: '/traceability-search?q=00370123450000000024' },
];

const MOCK_MACHINES: SearchResult[] = [
  { id: 'CNC-A2', label: 'CNC-A2 · Corte CNC', sub: 'LINE-A · Estación 2', path: '/condition-monitoring?asset=CNC-A2' },
  { id: 'ROB-B1', label: 'ROB-B1 · Soldadura', sub: 'LINE-B · Estación 1', path: '/condition-monitoring?asset=ROB-B1' },
  { id: 'ST-A3', label: 'Estación ST-A3', sub: 'Ensamblaje · LINE-A', path: '/device-manager' },
];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Global search input for the TopHeader.
 *
 * Renders a debounced (300ms) live-search dropdown grouped by entity type
 * (Work Orders, Serial Numbers, SSCC pallets, Machines/Stations). Selecting
 * a result navigates to the relevant detail page. The trailing ⌘K button
 * opens the global CommandPalette for broader navigation.
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(query.trim().toLowerCase(), 300);
  const inputRef = useRef<HTMLInputElement>(null);

  const groups: ResultGroup[] = useMemo(() => {
    if (!debounced) return [];
    const q = debounced;

    const woResults: SearchResult[] = listWorkOrders()
      .filter(
        (w) =>
          w.id.toLowerCase().includes(q) ||
          w.customerName.toLowerCase().includes(q) ||
          w.productDescription.toLowerCase().includes(q),
      )
      .slice(0, 5)
      .map((w) => ({
        id: w.id,
        label: w.id,
        sub: `${w.productDescription} · ${w.customerName}`,
        path: `/wo/${w.id}`,
      }));

    const match = (r: SearchResult) =>
      r.id.toLowerCase().includes(q) ||
      r.label.toLowerCase().includes(q) ||
      (r.sub?.toLowerCase().includes(q) ?? false);

    return [
      { heading: 'Órdenes de Trabajo', Icon: Package, items: woResults },
      { heading: 'Números de Serie', Icon: Hash, items: MOCK_SN.filter(match).slice(0, 5) },
      { heading: 'Lotes / SSCC', Icon: QrCode, items: MOCK_SSCC.filter(match).slice(0, 5) },
      { heading: 'Máquinas / Estaciones', Icon: Cpu, items: MOCK_MACHINES.filter(match).slice(0, 5) },
    ].filter((g) => g.items.length > 0);
  }, [debounced]);

  const totalResults = groups.reduce((n, g) => n + g.items.length, 0);
  const showDropdown = open && debounced.length > 0;

  const handleSelect = (path: string) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  return (
    <div className="relative hidden md:flex items-center gap-1" role="search">
      <Popover open={showDropdown} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="global-search-input" className="sr-only">{t('header.search')}</label>
            <Input
              id="global-search-input"
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => query && setOpen(true)}
              placeholder={t('header.search')}
              aria-label={t('header.search')}
              className="h-8 w-64 pl-8 pr-12 text-xs bg-secondary border-0"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                openPalette(true);
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title="Abrir paleta de comandos (Ctrl/⌘+K)"
              aria-label="Abrir paleta de comandos"
            >
              <Command className="h-2.5 w-2.5" />K
            </button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[420px] p-0 max-h-[60vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {totalResults === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center">
              Sin resultados para "{query}"
            </div>
          ) : (
            <div className="py-1">
              {groups.map((g) => (
                <div key={g.heading}>
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <g.Icon className="h-3 w-3" />
                    {g.heading}
                  </div>
                  {g.items.map((it) => (
                    <button
                      key={`${g.heading}-${it.id}`}
                      onClick={() => handleSelect(it.path)}
                      className="w-full text-left px-3 py-1.5 hover:bg-accent transition-colors flex flex-col"
                    >
                      <span className="text-xs font-medium">{it.label}</span>
                      {it.sub && (
                        <span className="text-[10px] text-muted-foreground truncate">{it.sub}</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
